import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Card,
    CardContent,
    Divider,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
    InputLabel,
    TextField,
    CardHeader,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';

import { useNotification } from 'config/Notification';
import {
    fetchCompanyStandardsByCompany as fetchCompanyStandardsByCompanyService,
    saveNewControlByCompanyStandard as saveNewControlByCompanyStandardService,
    fetchControlsByCompanyStandardId as fetchControlsByCompanyStandardIdService,
    updateCompanyControlsById as updateCompanyControlsByIdService,
} from 'services/modules/cap/standardservice';
import Breadcrumbs from 'config/Breadcrumb';


const ConfigureControls = () => {
    const standardTypes = [
        { id: 'domain-based', name: 'Domain Based' }
    ];

    // React router
    const navigate = useNavigate();

    // Notification
    const { openNotification } = useNotification();

    // State Variables
    const [standardType, setStandardType] = useState(standardTypes[0].id);
    const [activeSubcontrol, setActiveSubcontrol] = useState(false);
    const [companyId, setCompanyId] = useState(null);
    const [selectedStandards, setSelectedStandards] = useState([]);
    const [controls, setControls] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isStep2Editing, setIsStep2Editing] = useState(false);
    const [editControlId, setEditControlId] = useState(null); 
    const [openStep2, setOpenStep2] = useState(false);

    // Redux selectors
    const selectedCompanyContainer = useSelector((state) => state.auth.companyData);

    // Fetch standards data based on the selected standard type
    const fetchStandardsData = async () => {
        try {
            let group = standardType === 'standard-based' ? 'Main Standard' : 'Domain Standard';
            const response = await fetchCompanyStandardsByCompanyService({ group, companyId }

            );
            setSelectedStandards(response.data);
        } catch (error) {
            openNotification("Error while fetching data", 'error');
        }
    };

    // Fetch controls based on the selected standard
    const fetchControlsDataByStandard = async (standard) => {
        try {
            const params = { Standard: standard, company: companyId };
            const response = await fetchControlsByCompanyStandardIdService(params);
            setControls(response.data || []);
        } catch (error) {
            openNotification("Error while fetching controls", 'error');
        }
    };

    useEffect(() => {
        if (selectedCompanyContainer) {
            setCompanyId(selectedCompanyContainer.companyid);
        }

    }, [selectedCompanyContainer]);


    // This method handles to change the pages
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // This method handles to change the rows per page based on user selection
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    // Validation schema for controls
    const validationSchemaStep2 = Yup.object({
        standard: Yup.string().required('Required'),
        controlId: Yup.string().required('Required'),
        slno: Yup.string().required('Required'),
        controlName: Yup.string().required('Required'),

    });
    // Formik setup for step 2 (control)
    const formikStep2 = useFormik({
        initialValues: {
            standardName: '',
            standard: '',
            controlId: '',
            slno: '',
            controlName: '',
            controlDescription: '',
            status: true
        },
        validationSchema: validationSchemaStep2,
        onSubmit: async (values) => {
            try {
                const body = {
                    "Standard": values.standardName,
                    "ControlID": values.controlId,
                    "slno": values.slno,
                    "Title": values.controlName,
                    "Description": values.controlDescription,
                    "company": companyId,
                    "status": values.status
                };

                if (isStep2Editing) {
                    // Update existing control
                    setActiveSubcontrol(false);
                    let controlId = editControlId;
                    let standarId = values.standard;
                    const response = await updateCompanyControlsByIdService(controlId, standarId, { body });
                    if (response.status === 200) {
                        openNotification("Control updated successfully", 'success');
                        fetchControlsDataByStandard(values.standardName);
                        let standardName = values.standardName;
                        let standard = values.standard;

                        formikStep2.resetForm();
                        formikStep2.setFieldValue('standardName', standardName);
                        formikStep2.setFieldValue('standard', standard);
                        setIsStep2Editing(false); 
                        setEditControlId(null); 
                    }
                } else {
                    // Save new control
                    const response = await saveNewControlByCompanyStandardService({ standard: values.standard, body: body });
                    if (response.status === 201) {
                        openNotification("Control saved successfully", 'success');
                        setActiveSubcontrol(true);
                        fetchControlsDataByStandard(values.standardName);
                        let standardName = values.standardName;
                        let standard = values.standard;

                        formikStep2.resetForm();
                        formikStep2.setFieldValue('standardName', standardName);
                        formikStep2.setFieldValue('standard', standard);
                    }
                }
            } catch (error) {
                // Code change for control id error
                if (error?.response?.status === 400) {
                    openNotification(
                        `ControlID ${values.controlId} already exists. Please enter a different ControlID`,
                        "error"
                      );
                } else {
                    openNotification("Error while saving control", 'error');
                }
            }
        }

    });

    // Method handles to edit the control details
    const handleEditControlClick = (control) => {
        setIsStep2Editing(true);
        setEditControlId(control.id); 
        // Update only the specific fields in the Formik form
        formikStep2.setFieldValue('controlId', control.ControlID);
        formikStep2.setFieldValue('slno', control.slno);
        formikStep2.setFieldValue('controlName', control.Title);
        formikStep2.setFieldValue('controlDescription', control.Description ?? '');
        formikStep2.setFieldValue('status', control.status);

    };

    // Reset the form based on standardtype and company
    useEffect(() => {
        if (companyId) {
            fetchStandardsData();
            formikStep2.resetForm();
            setIsStep2Editing(false);
            setControls([]);
        }
         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [standardType, companyId]);

    useEffect(() => {
        // Find the selected standard name
        if (formikStep2.values.standard) {
            const selectedStandard = selectedStandards.find(
                (standard) => standard.id === formikStep2.values.standard
            );

            if (selectedStandard) {
                formikStep2.setFieldValue('standardName', selectedStandard.stdid);
            }
            fetchControlsDataByStandard(selectedStandard.stdid);
        }
         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formikStep2.values.standard]);

    useEffect(() => {
        if (controls.length > 0) {
            formikStep2.setFieldValue('slno', (controls.length + 1));
        } else {
            formikStep2.setFieldValue('slno', (1));
        }
         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controls])

    // Method handles to change the status of the control
    const handleStep2StatusChange = (event) => {
        const newStatus = event.target.value === 'true';
        if (!newStatus) { 
            setOpenStep2(true); 

        } else {
            formikStep2.setFieldValue('status', true); 
        }
    };

    // MEthod handles to confirm the status to inactive for control
    const handleStep2ConfirmInactive = () => {
        formikStep2.setFieldValue('status', false);
        setOpenStep2(false);
    };


    return (
        <div className='main-cont'>
            <Card>
                <CardHeader
                    title={
                        <React.Fragment>
                            <div className="card-head">
                                <div className="flex-head">
                                    <label className="label">
                                        <Breadcrumbs paths={['CAP Configurator', 'Configure Controls']} />
                                    </label>
                                </div>
                                <div>
                                    <Button
                                        variant="contained"
                                        className="back font"
                                        onClick={() => window.history.back()}
                                    >
                                        Back
                                    </Button>
                                </div>
                            </div>
                        </React.Fragment>
                    }
                />
                <CardContent>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Select Standard Type</FormLabel>
                        <RadioGroup
                            aria-label="standard-type"
                            name="standardType"
                            value={standardType}
                            onChange={(e) => setStandardType(e.target.value)}
                            className='d-flex flex-row gap-2'
                        >
                            {standardTypes.map((type) => (
                                <FormControlLabel
                                    key={type.id}
                                    value={type.id}
                                    control={<Radio />}
                                    label={type.name}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                    <Divider className='my-1' />
                    {standardType && (
                        <>
                            <CardContent className='loader-container '>
                                <div className='d-flex justify-content-center mb-4'>
                                    <Card className="row company-screen-bg col-md-10">
                                        <CardContent>
                                            <form onSubmit={formikStep2.handleSubmit}>
                                                <div className="row ">
                                                    <FormControl className='col-md-6 px-2' margin="normal">
                                                        <InputLabel htmlFor="standard">Standard</InputLabel>
                                                        <Select
                                                            name="standard"
                                                            label="Standard"
                                                            value={formikStep2.values.standard}
                                                            onChange={formikStep2.handleChange}
                                                            error={formikStep2.touched.standard && Boolean(formikStep2.errors.standard)}
                                                        >
                                                            {selectedStandards && selectedStandards.map((standard) => (
                                                                <MenuItem key={standard.id} value={standard.id}>
                                                                    {standard.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        {formikStep2.touched.standard && formikStep2.errors.standard && (
                                                            <div className="error-message">{formikStep2.errors.standard}</div>
                                                        )}
                                                    </FormControl>
                                                    <FormControl className='col-md-6 px-2' margin="normal">
                                                        <TextField
                                                            label="Control Id"
                                                            name="controlId"
                                                            disabled={standardType === 'standard-based'} 
                                                            onChange={formikStep2.handleChange}
                                                            value={formikStep2.values.controlId}
                                                            error={formikStep2.touched.controlId && Boolean(formikStep2.errors.controlId)}
                                                            helperText={formikStep2.touched.controlId && formikStep2.errors.controlId}
                                                        />
                                                    </FormControl>
                                                </div>

                                                <FormControl className='col-md-6 px-2' margin="normal">
                                                    <TextField
                                                        label="Control Name"
                                                        name="controlName"
                                                        disabled={standardType === 'standard-based'}
                                                        onChange={formikStep2.handleChange}
                                                        value={formikStep2.values.controlName}
                                                        error={formikStep2.touched.controlName && Boolean(formikStep2.errors.controlName)}
                                                        helperText={formikStep2.touched.controlName && formikStep2.errors.controlName}
                                                    />
                                                </FormControl>
                                                <FormControl className='col-md-6 px-2' margin="normal">
                                                    <TextField
                                                        label="Control Description"
                                                        name="controlDescription"
                                                        disabled={standardType === 'standard-based'}
                                                        onChange={formikStep2.handleChange}
                                                        value={formikStep2.values.controlDescription}
                                                        error={formikStep2.touched.controlDescription && Boolean(formikStep2.errors.controlDescription)}
                                                        helperText={formikStep2.touched.controlDescription && formikStep2.errors.controlDescription}
                                                        multiline
                                                        rows={1} 
                                                        maxRows={Infinity} 
                                                    />
                                                </FormControl>
                                                {isStep2Editing && (
                                                    <>
                                                        <div className='row col-md-12 px-2 align-items-center'>
                                                            <FormControl component="fieldset" className='d-flex' margin="normal">
                                                                <RadioGroup
                                                                    row
                                                                    aria-label="status"
                                                                    name="status"
                                                                    value={formikStep2.values.status ? 'true' : 'false'}
                                                                    onChange={handleStep2StatusChange}
                                                                >
                                                                    <FormControlLabel value="true" control={<Radio />} label="Active" />
                                                                    <FormControlLabel value="false" control={<Radio />} label="Inactive" />
                                                                </RadioGroup>
                                                            </FormControl>
                                                        </div>
                                                    </>
                                                )}

                                                <div  className=" d-flex justify-content-center align-items-center mt-2">
                                                    <Button color="primary" variant="contained"
                                                        className='mx-2 custom-width'
                                                        disabled={(!isStep2Editing && standardType === 'standard-based')}
                                                        type="submit">
                                                        {isStep2Editing ? 'Update' : 'Save'}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        className="danger mx-2 custom-width"
                                                        disabled={(!isStep2Editing && standardType === 'standard-based')}
                                                        onClick={() => {
                                                            let standardName = formikStep2.values.standardName;
                                                            let standard = formikStep2.values.standard;

                                                            formikStep2.resetForm();
                                                            formikStep2.setFieldValue('standardName', standardName);
                                                            formikStep2.setFieldValue('standard', standard);
                                                            formikStep2.setFieldValue('slno', controls.length > 0 ? controls.length + 1 : 1);
                                                            setIsStep2Editing(false);
                                                            setEditControlId(null);
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>

                                                    {activeSubcontrol && (
                                                        <Button
                                                            variant="contained"
                                                            className="mx-3"
                                                            style={{ maxWidth: '280px', minWidth: '150px', }}
                                                            onClick={() => {
                                                                navigate("/cap/adm-configurequestion");
                                                            }}
                                                        >
                                                            Subcontrol Configuration
                                                        </Button>
                                                    )}

                                                </div>
                                                <Dialog
                                                    open={openStep2}
                                                    onClose={()=> setOpenStep2(false)}
                                                    aria-labelledby="alert-dialog-title"
                                                    aria-describedby="alert-dialog-description"
                                                >
                                                    <DialogTitle id="alert-dialog-title">{"Confirm Inactive Status"}</DialogTitle>
                                                    <DialogContent>
                                                        <DialogContentText id="alert-dialog-description">
                                                            Would you really like to change the status to "Inactive"?.
                                                        </DialogContentText>
                                                    </DialogContent>
                                                    <DialogActions>
                                                        <Button onClick={()=> setOpenStep2(false)} color="primary">
                                                            Cancel
                                                        </Button>
                                                        <Button onClick={handleStep2ConfirmInactive} color="secondary" autoFocus>
                                                            Yes
                                                        </Button>
                                                    </DialogActions>
                                                </Dialog>
                                            </form>
                                        </CardContent>
                                    </Card>
                                </div>

                                <div className='mb-4'>
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ justifyContent: 'center', textAlign: 'center' }} >Control Id</TableCell>
                                                    <TableCell>Control Name</TableCell>
                                                    <TableCell>Description</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Actions</TableCell>

                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {controls.map((control, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell sx={{ justifyContent: 'center', textAlign: 'center' }} >{control.ControlID}</TableCell>
                                                        <TableCell>{control.Title}</TableCell>
                                                        <TableCell className='custom-wrap' >
                                                            {control.Description}
                                                        </TableCell>
                                                        <TableCell>{control.status ? 'Active' : 'Inactive'}</TableCell>

                                                        <TableCell>
                                                            <Button
                                                                variant="contained"
                                                                color="secondary"
                                                                onClick={() => handleEditControlClick(control)}
                                                            >
                                                                Edit
                                                            </Button>
                                                        </TableCell>

                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <TablePagination
                                        rowsPerPageOptions={[10, 25, 100]}
                                        component="div"
                                        count={controls.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                </div>
                                <Divider className='my-1'/>
                            </CardContent>

                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
export default ConfigureControls;
